import { useEffect, useId, type ComponentProps, type ReactNode } from 'react';
import { create } from 'zustand';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface VideoActionsDropdownState {
  activeId: string | null;
  setOpen: (id: string, open: boolean) => void;
}

// Card drag handlers stop pointer propagation, so outside-click dismissal alone
// cannot ensure exclusivity. A late close from an old menu must not close its successor.
const useVideoActionsDropdown = create<VideoActionsDropdownState>((set) => ({
  activeId: null,
  setOpen: (id, open) => set((state) => ({
    activeId: open ? id : state.activeId === id ? null : state.activeId,
  })),
}));

export function VideoActionsDropdown({ trigger, children, contentProps }: {
  trigger: ReactNode;
  children: ReactNode;
  contentProps?: ComponentProps<typeof DropdownMenuContent>;
}) {
  const id = useId();
  const open = useVideoActionsDropdown((state) => state.activeId === id);
  const setOpen = useVideoActionsDropdown((state) => state.setOpen);
  useEffect(() => () => setOpen(id, false), [id, setOpen]);

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={(next) => setOpen(id, next)}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        {...contentProps}
        onCloseAutoFocus={(event) => {
          contentProps?.onCloseAutoFocus?.(event);
          const activeId = useVideoActionsDropdown.getState().activeId;
          // Switching menus should focus the new menu; Escape still returns
          // focus to this menu's own trigger through the normal Radix behavior.
          if (activeId && activeId !== id) event.preventDefault();
        }}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
